import { useCallback, useMemo, useRef, useState } from 'react';
import { User, useImpersonateUserMutation } from '../api';
import { useEntityActions } from '@/hooks/useEntityActions';
import { useHashModal } from '@/hooks/useHashModal';
import { startUserImpersonation } from '../impersonation';
import { toaster } from '@/components/ui/Toaster';

// Define user actions (no delete action, roles instead of organization)
export type UserAction = 
  | 'view' 
  | 'edit' 
  | 'edit_configuration'
  | 'change_email' 
  | 'forgot_password' 
  | 'change_password' 
  | 'change_roles'
  | 'change_slug'
  | 'clone_profile'
  | 'copy_conditions'
  | 'manage_doctors';

// Define getEntityId outside to ensure stability
const getEntityId = (user: User) => user.id;

export const useUserActions = () => {
  const hashModal = useHashModal();
  const [impersonateUser] = useImpersonateUserMutation();
  const [pendingImpersonationUserId, setPendingImpersonationUserId] = useState<number | null>(null);
  const impersonationInFlightRef = useRef(false);

  const baseActions = useEntityActions<User, UserAction>({
    entityName: 'User',
    displayProperty: 'email',
    deleteMutation: () => [() => {}, { isLoading: false }],
    getEntityId,
    additionalActions: {
      change_email: 'change-email',
      forgot_password: 'forgot-password',
      change_password: 'change-password',
      change_roles: 'change-roles',
      change_slug: 'change-slug',
      clone_profile: 'clone-profile',
      copy_conditions: 'copy-conditions',
      manage_doctors: 'manage-doctors',
      edit_configuration: 'edit-configuration',
    },
  });

  const handleCreateAssistant = useCallback(() => {
    hashModal.navigateToModal('new');
  }, [hashModal.navigateToModal]);

  const handleAction = useCallback((action: UserAction, user: User) => {
    baseActions.handleAction(action, user);
  }, [baseActions]);

  const handleImpersonateById = useCallback(async (userId: number) => {
    if (impersonationInFlightRef.current) {
      return;
    }

    impersonationInFlightRef.current = true;
    setPendingImpersonationUserId(userId);

    let result;
    try {
      result = await startUserImpersonation(
        userId,
        (id) => impersonateUser(id).unwrap()
      );
    } finally {
      impersonationInFlightRef.current = false;
      setPendingImpersonationUserId(null);
    }

    if (result.errorMessage) {
      toaster.create({
        type: 'error',
        title: 'Failed to start impersonation',
        description: result.errorMessage,
      });
    }
  }, [impersonateUser]);

  const handleImpersonateUser = useCallback((user: User) => {
    void handleImpersonateById(user.id);
  }, [handleImpersonateById]);

  return useMemo(() => ({
    ...baseActions,
    handleAction,
    handleCreateAssistant,
    handleImpersonateUser,
    handleImpersonateById,
    isImpersonatingUser: pendingImpersonationUserId !== null,
  }), [
    baseActions,
    handleAction,
    handleCreateAssistant,
    handleImpersonateUser,
    handleImpersonateById,
    pendingImpersonationUserId
  ]);
};
